import { Image, Loader2, Send, Video } from '@/lib/icons'
import { formatNumber } from '@ayub/shared'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'

export interface PreviewDialogProps {
  open: boolean
  audienceSummary: string
  deliverableCount: number
  botBlockedCount: number
  textUz: string
  textRu: string
  fileId: string
  fileType: 'photo' | 'video'
  isSubmitting: boolean
  errorMessage?: string | null
  onConfirm: () => void
  onCancel: () => void
}

/**
 * "👁 Ko'rib chiqish" (`docs/03-app-flow.md` Sahifa 5) — xabar ikkala tilda va kimga
 * ketishi ko'rsatiladi, TASDIQLASH shu oynaning ichida ("Yuborish" tugmasi). Ataylab
 * alohida "to'g'ridan-to'g'ri yuborish" tugmasi YO'Q (`BroadcastPage.tsx`da faqat
 * "Ko'rib chiqish" bor) — yuzlab/minglab blogerga ketadigan, ORQAGA QAYTARIB BO'LMAYDIGAN
 * amal (`docs/04-ui-ux.md`: "Xavfli amallar... doim tasdiq oynasi bilan") oldindan
 * ko'rmasdan yuborilmasin degan qaror.
 */
export function PreviewDialog({
  open,
  audienceSummary,
  deliverableCount,
  botBlockedCount,
  textUz,
  textRu,
  fileId,
  fileType,
  isSubmitting,
  errorMessage,
  onConfirm,
  onCancel,
}: PreviewDialogProps) {
  return (
    <Modal open={open} onClose={onCancel} ariaLabel="Xabarni ko'rib chiqish" zIndex={50}>
      <div className="flex max-h-[90vh] flex-col">
        <div className="shrink-0 border-b border-app-border px-5 py-4">
          <h2 className="text-base font-semibold">Ko&apos;rib chiqish</h2>
          <p className="mt-1 text-sm text-app-hint">
            <span className="font-medium text-app-text">{audienceSummary}</span> —{' '}
            <span className="tabular font-medium text-app-text">
              {formatNumber(deliverableCount)} ta
            </span>{' '}
            blogerga ketadi
            {botBlockedCount > 0 && (
              <>
                {' '}
                (yana <span className="tabular">{formatNumber(botBlockedCount)} ta</span> botni
                bloklagani uchun o&apos;tkazib yuboriladi)
              </>
            )}
            .
          </p>
        </div>

        <div className="flex flex-col gap-3 overflow-y-auto px-5 py-4">
          <MessageBubble langLabel="O'zbekcha" text={textUz} fileId={fileId} fileType={fileType} />
          <MessageBubble langLabel="Ruscha" text={textRu} fileId={fileId} fileType={fileType} />
        </div>

        <div className="flex shrink-0 flex-col gap-2 border-t border-app-border px-5 py-4">
          {errorMessage && (
            <p className="text-xs font-medium text-urgency-overdue-text">{errorMessage}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              Orqaga
            </Button>
            <Button onClick={onConfirm} disabled={isSubmitting || deliverableCount === 0}>
              {isSubmitting ? (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              ) : (
                <Send size={16} aria-hidden="true" />
              )}
              Yuborish
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

function MessageBubble({
  langLabel,
  text,
  fileId,
  fileType,
}: {
  langLabel: string
  text: string
  fileId: string
  fileType: 'photo' | 'video'
}) {
  const MediaIcon = fileType === 'video' ? Video : Image

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-app-hint">{langLabel}</span>
      <div className="rounded-card border border-app-border bg-app-bg p-3">
        {fileId.length > 0 && (
          // Haqiqiy rasm/video ko'rsatib bo'lmaydi — `fileId` faqat Telegram serverida
          // ma'noga ega, WebApp'dan to'g'ridan-to'g'ri URL sifatida yuklab bo'lmaydi
          // (`MessageComposer.tsx`dagi qarorga qarang). Shuning uchun o'rniga joy egallovchi
          // bloк ko'rsatiladi — admin hech bo'lmasa "media biriktirilgani"ni ko'radi.
          <div className="mb-2 flex h-24 items-center justify-center gap-2 rounded-input bg-app-card text-app-hint">
            <MediaIcon size={20} aria-hidden="true" />
            <span className="text-xs">{fileType === 'video' ? 'Video' : 'Rasm'} biriktirilgan</span>
          </div>
        )}
        <p className="whitespace-pre-wrap text-sm text-app-text">
          {text.trim().length > 0 ? (
            text
          ) : (
            <span className="text-app-hint">(matn kiritilmagan)</span>
          )}
        </p>
      </div>
    </div>
  )
}
